import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Inject,
  Input,
  OnInit,
  Optional,
  Renderer2,
  Self,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { NgbNavItem } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject } from 'rxjs';
import { delay } from 'rxjs/operators';

import { NgControlDirective } from '../utils/typed-forms';
import { AuthorizeBase } from './authorize.base';
import { Authorizer, AUTHORIZERS } from './authorizer';

export type HTMLEnableElement =
  | HTMLButtonElement
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement
  | HTMLOptGroupElement
  | HTMLOptionElement;

export type NgControlDirectiveWithSetDisabledState = NgControlDirective & {
  valueAccessor: ControlValueAccessor & { setDisabledState: (isDisabled: boolean) => void };
};

@Directive({
  selector:
    ':not([formControl]):not([formControlName]):not([ngModel]):not([ngbNavItem])[authorizeEnable]',
  standalone: false,
})
export class AuthorizeEnableDirective extends AuthorizeBase implements OnInit {
  private disabledExternally$ = new BehaviorSubject<boolean | null>(null);

  get component(): {} | null {
    // Angular internal black magic
    // https://github.com/angular/angular/blob/12.2.x/packages/core/src/render3/interfaces/view.ts#L26-L57
    // HOST = 0
    // CONTEXT = 8
    return (this.viewContainerRef as any)._lContainer?.[0]?.[8] ?? null;
  }

  @Input() set disabled(value: boolean | undefined) {
    this.disabledExternally$.next(value ?? null);
  }

  constructor(
    @Optional() @Self() @Inject(AUTHORIZERS) authorizers: Authorizer[],
    protected renderer: Renderer2,
    protected elementRef: ElementRef<HTMLElement>,
    public viewContainerRef: ViewContainerRef,
    private cd: ChangeDetectorRef,
  ) {
    super(authorizers);
  }

  ngOnInit() {
    super.ngOnInit();

    this._subscriptions.push(
      this.disabledExternally$.pipe(delay(1)).subscribe(() => {
        this.toggleAuthorized(this.isAuthorized);
      }),
    );

    if (this.component) {
      this.assertDisabledProperty(this.component);
    } else {
      this.assertDisabledAttribute(this.elementRef);
    }
  }

  toggleAuthorized(isAuthorized: boolean) {
    const isDisabled = !isAuthorized || (this.disabledExternally$.value ?? false);

    if (this.component) {
      this.assertDisabledProperty(this.component);
      this.component.disabled = isDisabled;
      this.cd.detectChanges();
    } else {
      this.assertDisabledAttribute(this.elementRef);
      this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    }
  }

  assertDisabledProperty(component: {}): asserts component is { disabled: boolean } {
    if (!('disabled' in component)) {
      throw new Error(
        'AuthorizeEnableDirective requires a component with a disabled property or a control',
      );
    }
  }
  assertDisabledAttribute(
    el: ElementRef<HTMLElement>,
  ): asserts el is ElementRef<HTMLEnableElement> {
    if (!('disabled' in el.nativeElement)) {
      throw new Error(
        'AuthorizeEnableDirective requires an element with a disabled property or a control',
      );
    }
  }
}

@Directive({
  selector:
    '[formControl][authorizeEnable], [formControlName][authorizeEnable], [ngModel][authorizeEnable]',
  standalone: false,
})
export class AuthorizeEnableControlDirective extends AuthorizeBase implements OnInit {
  private disabledExternally$ = new BehaviorSubject<boolean | null>(null);

  @Input() set disabled(value: boolean | undefined) {
    this.disabledExternally$.next(value ?? null);
  }

  constructor(
    @Optional() @Self() @Inject(AUTHORIZERS) authorizers: Authorizer[],
    @Self() @Inject(NgControl) protected ngControl: NgControlDirective,
  ) {
    super(authorizers);
  }

  ngOnInit() {
    super.ngOnInit();

    this._subscriptions.push(
      this.disabledExternally$.pipe(delay(1)).subscribe(() => {
        this.toggleAuthorized(this.isAuthorized);
      }),
    );

    this.assertControlAccessor(this.ngControl);
    this.ngControl.control.registerOnDisabledChange(isDisabled =>
      this.disabledExternally$.next(isDisabled),
    );
  }

  toggleAuthorized(isAuthorized: boolean) {
    const isDisabled = !isAuthorized || (this.disabledExternally$.value ?? false);

    this.assertControlAccessor(this.ngControl);
    this.ngControl.valueAccessor.setDisabledState(isDisabled);
  }

  assertControlAccessor(
    ngControl: NgControlDirective,
  ): asserts ngControl is NgControlDirectiveWithSetDisabledState {
    if (!ngControl.valueAccessor?.setDisabledState) {
      throw new Error(
        'AuthorizeEnableControlDirective requires a valueAccessor with setDisabledState',
      );
    }
  }
}

@Directive({
  selector: '[ngbNavItem][authorizeEnable]',
  standalone: false,
})
export class AuthorizeEnableNavItemDirective extends AuthorizeBase implements OnInit {
  private disabledExternally$ = new BehaviorSubject<boolean | null>(null);

  @Input() set disabled(value: boolean | undefined) {
    this.disabledExternally$.next(value ?? null);
  }

  constructor(
    @Optional() @Self() @Inject(AUTHORIZERS) authorizers: Authorizer[],
    @Self() protected ngbNavItem: NgbNavItem,
  ) {
    super(authorizers);
  }

  ngOnInit() {
    super.ngOnInit();

    this._subscriptions.push(
      this.disabledExternally$.pipe(delay(1)).subscribe(() => {
        this.toggleAuthorized(this.isAuthorized);
      }),
    );
  }

  toggleAuthorized(isAuthorized: boolean) {
    const isDisabled = !isAuthorized || (this.disabledExternally$.value ?? false);

    this.ngbNavItem.disabled = isDisabled;
  }
}
